/**
 * Répartition du stockage par type (pie chart).
 * Conforme WCAG 2.1 AA.
 */
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface StorageBreakdownProps {
  data: {
    media_storage_bytes: number;
    embeddings_storage_bytes: number;
    transcriptions_storage_bytes: number;
  };
}

export default function StorageBreakdown({ data }: StorageBreakdownProps) {
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const chartData = [
    { name: 'Médias', value: data.media_storage_bytes, color: '#3b82f6' },
    { name: 'Embeddings', value: data.embeddings_storage_bytes, color: '#8b5cf6' },
    { name: 'Transcriptions', value: data.transcriptions_storage_bytes, color: '#10b981' },
  ].filter(item => item.value > 0);

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
        <p className="text-center text-zinc-500 dark:text-zinc-400">Aucune donnée de stockage disponible</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pie chart */}
        <div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.name}: ${((entry.value / total) * 100).toFixed(1)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => formatBytes(value)} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Legend with details */}
        <div className="flex flex-col justify-center space-y-4">
          {chartData.map((item) => (
            <div key={item.name} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: item.color }}
                  aria-hidden="true"
                />
                <span className="font-medium text-zinc-900 dark:text-white">{item.name}</span>
              </div>
              <span className="text-zinc-600 dark:text-zinc-400">{formatBytes(item.value)}</span>
            </div>
          ))}
          <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700">
            <div className="flex items-center justify-between font-bold text-zinc-900 dark:text-white">
              <span>Total</span>
              <span>{formatBytes(total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
